/**
 * What a scan DOES, as a function of where it was scanned.
 *
 * This is deliberately not inside the shell's effect. The shell's effect is where
 * the fake-accept lived - a 120ms timer that settled every scan green without a
 * request - and it was invisible to every test in the repo because a test cannot
 * reach into a `useEffect`. Pulled out here it is a pure decision plus one injected
 * writer, and `scanDispatch.test.ts` can assert the thing that actually matters:
 * that a scan hits the server, and that an unwired station refuses.
 *
 * The governing rule, from Mitali: if something can go wrong because someone has to
 * remember to do it, the system should prevent it, flag it or prompt us. Nobody can
 * remember which stations are wired. So the default here is REJECT, and a station
 * becomes accepting only by having a writer named for it below.
 */
import { writes, type MovementReason, type MovementResult } from '../data/writes'

export interface ScanTarget {
  /** The route the operator is standing on. */
  path: string
  /** The movement bar's chosen reason, null until they tap one. */
  movementReason: MovementReason | null
}

export interface ScanOutcome {
  result: 'accept' | 'reject'
  message: string
  /** The row the server says it wrote. Present only on an accepted movement scan. */
  movement?: MovementResult
}

/** Only the writes a scan can trigger, so a test needs no network and no DOM. */
export interface ScanWriters {
  movementScan: typeof writes.movementScan
}

export const liveWriters: ScanWriters = { movementScan: writes.movementScan }

/**
 * Copy for a refusal. Every branch names something the operator can act on -
 * "not saved" is the word that must appear whenever nothing was written, because
 * that is the fact the old green tick concealed.
 */
function refusalCopy(code: string): string {
  switch (code) {
    case 'UNKNOWN_UNIT':
    case 'UNKNOWN_CODE':
      return 'Not saved - that is not a book we know. Check the label.'
    case 'CODE_RETIRED':
      return 'Not saved - that label was retired. Use the new one.'
    case 'FORBIDDEN':
      return 'Not saved - you do not have access to this. Ask Mitali.'
    case 'UNAUTHENTICATED':
      return 'Not saved - your session expired. Sign in again.'
    case 'offline':
      return 'Not saved - no connection. Nothing was sent.'
    default:
      return `Not saved - the server refused it (${code}).`
  }
}

function codeOf(e: unknown): string {
  if (e instanceof TypeError) return 'offline'
  if (e && typeof e === 'object' && 'code' in e) return String((e as { code: unknown }).code)
  return 'unknown'
}

export async function dispatchScan(
  target: ScanTarget,
  code: string | null,
  writers: ScanWriters = liveWriters,
): Promise<ScanOutcome> {
  if (!code) return { result: 'reject', message: 'Not saved - nothing was read. Scan again.' }

  if (target.path === '/movement') {
    if (!target.movementReason) {
      return { result: 'reject', message: 'Not saved - tap a reason first, then scan.' }
    }
    try {
      const res = await writers.movementScan(code, target.movementReason)
      return {
        result: 'accept',
        message: `Recorded - ${res.reason}. ${shortId(res.unitId)} is now ${spoken(res.state)}.`,
        movement: res,
      }
    } catch (e) {
      return { result: 'reject', message: refusalCopy(codeOf(e)) }
    }
  }

  /**
   * Every other station, until its write is wired. Named individually rather than
   * caught by a default so that wiring one is a visible edit here and forgetting to
   * remove its line is impossible - the line IS the refusal.
   */
  return {
    result: 'reject',
    message: `Not saved - the ${stationName(target.path)} station is not connected yet. Nothing was written.`,
  }
}

function stationName(path: string): string {
  switch (path) {
    case '/intake':
      return 'intake'
    case '/catalogue':
      return 'catalogue'
    case '/shelve':
      return 'shelve'
    case '/pick':
      return 'pick'
    case '/pack':
      return 'pack'
    default:
      return 'this'
  }
}

/** A unit id is 24 characters of no use to a human; the tail is what is on the label. */
function shortId(unitId: string): string {
  return unitId.length > 8 ? `...${unitId.slice(-6)}` : unitId
}

function spoken(state: string): string {
  return state.replace(/_/g, ' ')
}
